import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  TimePicker,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { format as rutFormat, validate as rutValidate } from 'rut.js';
import { getCommunes, getRegions, getUrlFile, uploadFiles } from '../../Services/declarations';
import { createThirdPartyClaim } from '../../Services/thirdPartyClaims';
import { HOME_ROUTE } from '../../Components/Router/constant';
import './ThirdPartyClaim.scss';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'CIA_SEGUROS', label: '¿Usted es Cía. de Seguros?' },
  { value: 'ABOGADO', label: '¿Usted es Abogado?' },
  { value: 'TERCERO', label: '¿Usted es el Tercero Involucrado?' },
];

const STEP_NAMES = [
  'Datos del Siniestro',
  'Datos de contacto',
  'Información del Bien - Tercero',
  'Adjuntar Información',
];

const NAMED_DOCUMENTS = [
  { key: 'padron_vehiculo_tercero', label: 'Padrón Vehículo tercero' },
  { key: 'foto_danos_vehiculo_tercero', label: 'Fotografía Daños Vehículo Tercero' },
  { key: 'foto_patente_tercero', label: 'Fotografía Patente Tercero' },
  { key: 'declaracion_siniestro_cia', label: 'Declaración de Siniestro Cía. de Seguros Tercero' },
  { key: 'carta_cobro_cia', label: 'Carta de Cobro Cía. de Seguros' },
  { key: 'foto_vehiculo_gama', label: 'Fotografía Vehículo Gama y/o Documentos Conductor vehículo Gama' },
  { key: 'presupuesto_reparacion', label: 'Presupuesto Reparación' },
  { key: 'factura_mano_obra', label: 'Factura Mano de Obra' },
  { key: 'factura_repuestos', label: 'Factura Repuestos' },
  { key: 'parte_policial', label: 'Parte Policial' },
  { key: 'cargar_otras_imagenes', label: 'Cargar otras Imágenes' },
];

const MAX_ADDITIONAL_DOCS = 15;

// Todos los documentos de Reclamo de Terceros se suben bajo UN mismo documentType;
// lo que los distingue es su nombre (documentName). Así el backend solo necesita ESTE
// código en document_types (antes exigía 26: 11 nombrados + 15 adicionales, y la carga
// de adicionales fallaba cuando esos códigos no estaban seedeados).
const TPC_DOCUMENT_TYPE = 'TPC_ATTACHMENT';

// ─── Field helpers (validación de patente y RUT) ─────────────────────────────

// Patente chilena: solo letras y números (sin caracteres especiales), máx. 6, en mayúsculas.
const PLATE_MAX_LENGTH = 6;
const normalizePlate = (value?: string): string =>
  (value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, PLATE_MAX_LENGTH);

// Teléfono: solo dígitos y caracteres propios de un número (+, espacio, guion, paréntesis).
const normalizePhone = (value?: string): string => (value || '').replace(/[^0-9+\-()\s]/g, '');

// Teléfono válido: al menos 9 dígitos. Permite vacío (el `required` lo cubre por separado).
const phoneRule = {
  validator: (_: unknown, value: string) => {
    const digits = (value || '').replace(/\D/g, '');
    return !value || digits.length >= 9
      ? Promise.resolve()
      : Promise.reject(new Error('Ingrese un teléfono válido (mínimo 9 dígitos)'));
  },
};

// Lista 1 — caracteres "especiales" (inyección/ruido, no gramaticales). Se eliminan
// al escribir en campos de texto libre. Se conservan & " ' y la puntuación normal
// (. , ; : ¿ ? ¡ ! ( ) - / # % ° º ª).
const SPECIAL_CHARS_RE = /[<>{}[\]\\|`~^=_*$]/g;
const stripSpecialChars = (value?: string): string => (value || '').replace(SPECIAL_CHARS_RE, '');

// Nombres de persona / razón social: LISTA BLANCA. Un blacklist no basta (los
// caracteres "raros" son infinitos: ! " ¿ ¡ ( ) ¨ …); acá se permite SOLO lo propio
// de un nombre y todo lo demás se elimina al escribir.
// Permitidos: letras (con tildes/ñ/ü), dígitos (razón social), espacio y . - '
const NAME_ALLOWED_RE = /[^A-Za-zÑñÁáÉéÍíÓóÚúÜü0-9.\-' ]/g;
const stripNameChars = (value?: string): string => (value || '').replace(NAME_ALLOWED_RE, '');

// Bloquea teclas que producen negativos o notación científica en campos numéricos de dinero.
const blockNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
};

// Formato de miles (CLP): punto cada 3 dígitos, solo enteros. Para <InputNumber>.
// El valor que guarda el form es el string de dígitos SIN puntos (igual que antes con
// `Input type="number"`): solo cambia la presentación. El submit lo convierte con Number().
const thousandsFormatter = (value?: string | number): string =>
  value === undefined || value === null || value === '' ? '' : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const thousandsParser = (value?: string): string => (value ?? '').replace(/\D/g, '');

// Documentos adjuntos: tipos permitidos y tamaño máximo, validados ANTES de subir.
// 10 MB para TODO (documentos, imágenes y videos), homologado con la declaración de
// Siniestro. El backend de subida temporal permite `fileSize: 1048576 * 10`.
const DOC_MAX_SIZE_MB = 10;
const DOC_ACCEPTED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const VIDEO_ACCEPTED_EXT = ['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv', '3gp'];
const DOC_ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.mp4,.mov,.webm,.m4v,.avi,.mkv,.3gp';
const isValidDocFile = (file: File): boolean => {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const typeOk = DOC_ACCEPTED_EXT.includes(ext) || VIDEO_ACCEPTED_EXT.includes(ext);
  if (!typeOk) {
    message.error('Solo puede subir archivos PDF, JPG, PNG, DOC, DOCX o videos (MP4, MOV, WEBM)');
    return false;
  }
  const sizeOk = (file.size ?? 0) / 1024 / 1024 <= DOC_MAX_SIZE_MB;
  if (!sizeOk) message.error(`El archivo supera el tamaño máximo de ${DOC_MAX_SIZE_MB} MB`);
  return sizeOk;
};

// RUT chileno con formato 12.345.678-9 (máx. 12 caracteres ya formateado).
const RUT_MAX_LENGTH = 12;
const normalizeRut = (value?: string): string => {
  const cleaned = (value || '').replace(/[^0-9kK]/g, '');
  return cleaned ? rutFormat(cleaned) : '';
};

// Regla antd que valida el dígito verificador del RUT. Permite vacío (el `required` lo cubre por separado).
const rutRule = {
  validator: (_: unknown, value: string) =>
    !value || rutValidate(value)
      ? Promise.resolve()
      : Promise.reject(new Error('RUT inválido, verifique el dígito verificador')),
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface UploadedDoc {
  key?: string;
  documentName: string;
  filename: string | null;
  temporaryKey: string | null;
  isAdditional: boolean;
  uploading?: boolean;
}

interface AdditionalSlot {
  name: string;
  filename: string | null;
  temporaryKey: string | null;
  uploading?: boolean;
}

// ─── Shared styles ───────────────────────────────────────────────────────────

const primaryBtn: React.CSSProperties = {
  backgroundColor: '#00B96B',
  color: 'white',
  borderRadius: '36px',
  border: 'none',
  height: '42px',
  minWidth: '160px',
};

const outlineBtn: React.CSSProperties = {
  borderColor: '#00B96B',
  color: '#00B96B',
  borderRadius: '36px',
  height: '42px',
  minWidth: '140px',
  background: 'white',
};

// ─── Sidebar step list ───────────────────────────────────────────────────────

function SidebarSteps({
  current,
  maxReached,
  onStepClick,
}: {
  current: number;
  maxReached: number;
  onStepClick: (i: number) => void;
}) {
  return (
    <div className="tpc-steps-box">
      {STEP_NAMES.map((name, i) => {
        const isActive = i === current;
        const isUnlocked = i <= maxReached; // etapa alcanzada → verde y clickeable
        return (
          <div
            key={i}
            role={isUnlocked ? 'button' : undefined}
            tabIndex={isUnlocked ? 0 : undefined}
            onClick={isUnlocked ? () => onStepClick(i) : undefined}
            onKeyDown={
              isUnlocked
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onStepClick(i);
                    }
                  }
                : undefined
            }
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              marginBottom: '24px',
              cursor: isUnlocked ? 'pointer' : 'default',
            }}
          >
            <div
              style={{
                marginTop: '3px',
                width: '10px',
                height: '10px',
                minWidth: '10px',
                borderRadius: '50%',
                backgroundColor: isUnlocked ? '#00B96B' : '#D9D9D9',
                opacity: isActive ? 1 : isUnlocked ? 0.5 : 1,
              }}
            />
            <span
              style={{
                fontSize: '13px',
                fontWeight: isActive ? 700 : 400,
                color: isActive ? '#262626' : isUnlocked ? '#00B96B' : '#595959',
                lineHeight: '1.4',
              }}
            >
              {name}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Datos del Siniestro ─────────────────────────────────────────────

function Step1Siniestro({ form }: { form: any }) {
  const idType = Form.useWatch('driverIdType', form);

  // Región/Comuna del siniestro: selectores en cascada poblados desde el API de ubicaciones
  // (/locations). Se guarda el NOMBRE (accidentRegion/accidentCommune), no el id.
  const [regions, setRegions] = useState<any[]>([]);
  const [communes, setCommunes] = useState<any[]>([]);

  const loadCommunes = (regionId?: string) => {
    if (!regionId) {
      setCommunes([]);
      return;
    }
    getCommunes(regionId)
      .then((res) => setCommunes(res?.data?.communes || []))
      .catch(() => setCommunes([]));
  };

  useEffect(() => {
    getRegions()
      .then((res) => {
        const regs = res?.data?.regions || [];
        setRegions(regs);
        // Al volver a la etapa con una región ya elegida, recargar sus comunas.
        const current = form.getFieldValue('accidentRegion');
        if (current) loadCommunes(regs.find((r: any) => r.regionName === current)?.id);
      })
      .catch(() => setRegions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegionChange = (regionName: string) => {
    form.setFieldsValue({ accidentCommune: undefined });
    loadCommunes(regions.find((r) => r.regionName === regionName)?.id);
  };

  return (
    <>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={8}>
          <Form.Item
            name="gamaVehiclePlate"
            label="Patente vehículo Gama *"
            normalize={normalizePlate}
            rules={[{ required: true, message: 'Debe ingresar la patente' }]}
          >
            <Input placeholder="Ej: ABCD12" maxLength={PLATE_MAX_LENGTH} style={{ textTransform: 'uppercase' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="gamaDriverName" label="Nombre completo del conductor Gama" normalize={stripNameChars}>
            <Input placeholder="Nombre completo" maxLength={200} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="driverIdType" label="Seleccionar documento identificador" initialValue="RUT">
            <Radio.Group>
              <Radio value="RUT">RUT</Radio>
              <Radio value="PASAPORTE">Pasaporte</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24} sm={8}>
          {/* Campos separados por tipo (con key): AntD preserva el valor del campo oculto
              al alternar; al enviar, el submit toma solo el del tipo seleccionado. */}
          {idType === 'PASAPORTE' ? (
            <Form.Item key="gamaDriverPassport" name="gamaDriverPassport" label="N° Pasaporte Conductor Gama">
              <Input placeholder="N° Pasaporte" maxLength={30} />
            </Form.Item>
          ) : (
            <Form.Item
              key="gamaDriverRut"
              name="gamaDriverRut"
              label="RUT Conductor Gama"
              normalize={normalizeRut}
              rules={[rutRule]}
            >
              <Input placeholder="Ej: 12.345.678-9" maxLength={RUT_MAX_LENGTH} />
            </Form.Item>
          )}
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="accidentDate"
            label="Fecha del siniestro *"
            rules={[{ required: true, message: 'Debe seleccionar la fecha' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              disabledDate={(current) => !!current && current.valueOf() > Date.now()}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="accidentTime"
            label="Hora del siniestro *"
            rules={[{ required: true, message: 'Debe ingresar la hora' }]}
          >
            <TimePicker style={{ width: '100%' }} format="HH:mm" placeholder="HH:MM" popupClassName="tpc-time-popup" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="accidentRegion"
            label="Región *"
            rules={[{ required: true, message: 'Debe seleccionar la región' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Seleccione la región"
              onChange={handleRegionChange}
              options={regions
                .slice()
                .sort((a, b) => (a?.regionName || '').localeCompare(b?.regionName || ''))
                .map((r) => ({ value: r.regionName, label: r.regionName }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="accidentCommune"
            label="Comuna *"
            rules={[{ required: true, message: 'Debe seleccionar la comuna' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Seleccione la comuna"
              disabled={!communes.length}
              options={communes
                .slice()
                .sort((a, b) => (a?.communeName || '').localeCompare(b?.communeName || ''))
                .map((c) => ({ value: c.communeName, label: c.communeName }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Form.Item
            name="accidentLocation"
            label="Lugar del siniestro *"
            normalize={stripSpecialChars}
            rules={[{ required: true, message: 'Debe ingresar el lugar' }]}
          >
            <Input placeholder="Dirección o descripción del lugar" maxLength={300} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Form.Item
            name="accidentDescription"
            label="Relato siniestro *"
            normalize={stripSpecialChars}
            rules={[{ required: true, message: 'Debe describir el siniestro' }]}
          >
            <TextArea rows={4} placeholder="Describa cómo ocurrió el siniestro..." maxLength={1000} showCount />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}

// ─── Step 2: Datos de contacto del Tercero ───────────────────────────────────

function Step2Contacto({ form, claimantRole }: { form: any; claimantRole: string }) {
  const needsInstitution = claimantRole === 'CIA_SEGUROS' || claimantRole === 'ABOGADO';
  const idType = Form.useWatch('claimantIdType', form);
  // Correo alternativo (opcional): se revela con el botón "+ Agregar otro correo".
  // Se inicializa abierto si ya hay un valor (p. ej. al volver a este paso).
  const [showAltEmail, setShowAltEmail] = useState<boolean>(
    () => !!form.getFieldValue('claimantEmailAlt'),
  );

  return (
    <>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={8}>
          <Form.Item
            name="claimantFullName"
            label="Nombre completo *"
            normalize={stripNameChars}
            rules={[{ required: true, message: 'Debe ingresar el nombre completo o razón social' }]}
          >
            <Input placeholder="Nombre completo" maxLength={200} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="claimantIdType" label="Tipo de identificación" initialValue="RUT">
            <Radio.Group>
              <Radio value="RUT">RUT</Radio>
              <Radio value="PASAPORTE">Pasaporte</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          {/* Campos separados para RUT y Pasaporte: cada tipo recuerda su propio
              valor al alternar (AntD preserva el valor del campo oculto). */}
          {idType === 'PASAPORTE' ? (
            <Form.Item
              key="claimantPassport"
              name="claimantPassport"
              label="N° Pasaporte *"
              rules={[{ required: true, message: 'Debe ingresar su identificación' }]}
            >
              <Input placeholder="N° Pasaporte" maxLength={30} />
            </Form.Item>
          ) : (
            <Form.Item
              key="claimantRut"
              name="claimantRut"
              label="RUT *"
              normalize={normalizeRut}
              rules={[
                { required: true, message: 'Debe ingresar su identificación' },
                rutRule,
              ]}
            >
              <Input placeholder="Ej: 12.345.678-9" maxLength={RUT_MAX_LENGTH} />
            </Form.Item>
          )}
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24} sm={16}>
          <Form.Item
            name="claimantEmail"
            label="Correo electrónico *"
            style={{ marginBottom: 4 }}
            rules={[
              { required: true, message: 'Debe ingresar el correo electrónico' },
              { type: 'email', message: 'El correo electrónico no es válido' },
            ]}
          >
            <Input placeholder="correo@ejemplo.com" maxLength={80} />
          </Form.Item>

          {!showAltEmail ? (
            <span
              role="button"
              tabIndex={0}
              onClick={() => setShowAltEmail(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowAltEmail(true);
                }
              }}
              style={{
                color: '#00B96B',
                cursor: 'pointer',
                fontWeight: 500,
                display: 'inline-block',
                marginTop: 0,
                marginBottom: 8,
                userSelect: 'none',
              }}
            >
              + Agregar otro correo
            </span>
          ) : (
            <Form.Item
              name="claimantEmailAlt"
              label="Correo electrónico alternativo"
              rules={[{ type: 'email', message: 'El correo electrónico no es válido' }]}
            >
              <Input
                placeholder="correo.alternativo@ejemplo.com"
                maxLength={80}
                addonAfter={
                  <Tooltip title="Quitar correo alternativo">
                    <DeleteOutlined
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        form.setFieldsValue({ claimantEmailAlt: undefined });
                        setShowAltEmail(false);
                      }}
                    />
                  </Tooltip>
                }
              />
            </Form.Item>
          )}
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="claimantPhone"
            label="Teléfono de contacto *"
            normalize={normalizePhone}
            rules={[{ required: true, message: 'Debe ingresar su teléfono' }, phoneRule]}
          >
            <Input placeholder="+56 9 1234 5678" maxLength={30} />
          </Form.Item>
        </Col>
      </Row>

      {needsInstitution && (
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="institutionName"
              label="Nombre Cía/Abogado/Otros"
              normalize={stripNameChars}
            >
              <Input placeholder="Ej: Compañía de Seguros ABC" maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="institutionRut"
              label="RUT de la institución"
              normalize={normalizeRut}
              rules={[rutRule]}
            >
              <Input placeholder="Ej: 76.123.456-7" maxLength={RUT_MAX_LENGTH} />
            </Form.Item>
          </Col>
        </Row>
      )}
    </>
  );
}

// ─── Step 3: Información del Bien - Tercero ──────────────────────────────────

const VEHICLE_TYPE_OPTIONS = ['Auto', 'Camión', 'Bus', 'Furgón', 'Camioneta', 'Motocicleta', 'Otro tipo de vehículo'];

// Opciones de material audiovisual que involucra al conductor/vehículo de Gama
const GAMA_MEDIA_OPTIONS = [
  { value: 'FOTOS', label: 'Solo fotografías' },
  { value: 'VIDEOS', label: 'Solo videos' },
  { value: 'AMBOS', label: 'Fotografías y videos' },
  { value: 'NINGUNO', label: 'No cuento con material audiovisual' },
];

/**
 * Grupo de radio buttons Sí / No reutilizable dentro del formulario.
 * Reenvía las props que Form.Item inyecta (value, onChange, id) al
 * Radio.Group interno; de lo contrario el valor nunca llega al form.
 */
function YesNoRadio(props: any) {
  return (
    <Radio.Group {...props}>
      <Radio value={true}>Sí</Radio>
      <Radio value={false}>No</Radio>
    </Radio.Group>
  );
}

function Step3Bien({ form }: { form: any }) {
  const propertyType = Form.useWatch('affectedPropertyType', form);
  const hasInsurance = Form.useWatch('thirdPartyHasInsurance', form);
  // Tipo de identificación del dueño del bien/vehículo: RUT (con formato y dígito
  // verificador) o Pasaporte (texto libre). Undefined se trata como RUT (default).
  const ownerIsPassport = Form.useWatch('thirdPartyOwnerIdType', form) === 'PASAPORTE';

  return (
    <>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Form.Item
            name="affectedPropertyType"
            label="Tipo de bien afectado *"
            rules={[{ required: true, message: 'Debe seleccionar el tipo' }]}
          >
            <Radio.Group>
              <Radio value="VEHICULO">Vehículo</Radio>
              <Radio value="OTRO">Otro bien y/o persona</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      {/* ── Rama: Vehículo ─────────────────────────────────────── */}
      {propertyType === 'VEHICULO' && (
        <>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="thirdPartyVehicleType"
                label="Tipo de Vehículo *"
                rules={[{ required: true, message: 'Debe seleccionar el tipo de vehículo' }]}
              >
                <Select placeholder="Seleccionar">
                  {VEHICLE_TYPE_OPTIONS.map((opt) => (
                    <Select.Option key={opt} value={opt}>
                      {opt}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="thirdPartyOwnerName"
                label="Nombre del dueño del vehículo *"
                normalize={stripNameChars}
                rules={[{ required: true, message: 'Debe ingresar el nombre completo o razón social' }]}
              >
                <Input placeholder="Nombre completo o razón social" maxLength={200} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="thirdPartyOwnerIdType"
                label="Tipo de identificación del dueño"
                initialValue="RUT"
                style={{ marginBottom: 8 }}
              >
                <Radio.Group>
                  <Radio value="RUT">RUT</Radio>
                  <Radio value="PASAPORTE">Pasaporte</Radio>
                </Radio.Group>
              </Form.Item>
              {ownerIsPassport ? (
                <Form.Item
                  key="thirdPartyOwnerPassport"
                  name="thirdPartyOwnerPassport"
                  label="N° Pasaporte del dueño *"
                  rules={[{ required: true, message: 'Debe ingresar la identificación' }]}
                >
                  <Input placeholder="N° Pasaporte" maxLength={30} />
                </Form.Item>
              ) : (
                <Form.Item
                  key="thirdPartyOwnerRut"
                  name="thirdPartyOwnerRut"
                  label="RUT del dueño *"
                  normalize={normalizeRut}
                  rules={[{ required: true, message: 'Debe ingresar el RUT' }, rutRule]}
                >
                  <Input placeholder="Ej: 12.345.678-9" maxLength={RUT_MAX_LENGTH} />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="thirdPartyVehiclePlate"
                label="Patente *"
                normalize={normalizePlate}
                rules={[{ required: true, message: 'Debe ingresar la patente' }]}
              >
                <Input placeholder="Ej: ABCD12" maxLength={PLATE_MAX_LENGTH} style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="thirdPartyVehicleBrand" label="Marca">
                <Input placeholder="Ej: Toyota" maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="thirdPartyVehicleModel" label="Modelo">
                <Input placeholder="Ej: Corolla" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item name="thirdPartyVehicleYear" label="Año">
                <Input type="number" placeholder="Ej: 2020" min={1980} max={2030} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="gamaMediaType"
                label="¿Cuenta con fotografías y/o videos que involucren al conductor o el vehículo de Gama? *"
                rules={[{ required: true, message: 'Debe seleccionar una opción' }]}
              >
                <Select placeholder="Seleccione" options={GAMA_MEDIA_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="thirdPartyHasInsurance"
                label="¿El dueño cuenta con Seguro? *"
                rules={[{ required: true, message: 'Debe seleccionar una opción' }]}
              >
                <YesNoRadio />
              </Form.Item>
            </Col>
          </Row>

          {/* Campos condicionales cuando el tercero cuenta con seguro */}
          {hasInsurance === true && (
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8}>
                <Form.Item name="insuranceClaimNumber" label="N° siniestro Cía. de Seguros">
                  <Input placeholder="N° de siniestro" maxLength={50} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="insuranceCompanyName"
                  label="Nombre Cía. Seguros *"
                  normalize={stripNameChars}
                  rules={[{ required: true, message: 'Debe ingresar el nombre completo o razón social' }]}
                >
                  <Input placeholder="Ej: Compañía de Seguros ABC" maxLength={200} />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="hasBodilyInjuries"
                label="¿Hubo lesiones corporales? *"
                rules={[{ required: true, message: 'Debe seleccionar una opción' }]}
              >
                <YesNoRadio />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="hasAlcoholTest"
                label="¿Se le realizó alcoholemia? *"
                rules={[{ required: true, message: 'Debe seleccionar una opción' }]}
              >
                <YesNoRadio />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="claimedAmount"
                label="Valor a Reclamar *"
                rules={[
                  { required: true, message: 'Debe ingresar el valor a reclamar' },
                  {
                    type: 'number',
                    min: 0,
                    transform: (value) => (value === '' || value === undefined ? undefined : Number(value)),
                    message: 'El valor no puede ser negativo',
                  },
                  {
                    type: 'number',
                    max: 1000000000,
                    transform: (value) => (value === '' || value === undefined ? undefined : Number(value)),
                    message: 'El valor no puede ser mayor a 1.000.000.000',
                  },
                  {
                    // Advertencia (no bloqueante): avisa si el valor a reclamar es 0.
                    warningOnly: true,
                    validator: (_, value) =>
                      value !== '' && value !== undefined && value !== null && Number(value) === 0
                        ? Promise.reject(new Error('El valor ingresado es 0, verifique que sea correcto'))
                        : Promise.resolve(),
                  },
                ]}
              >
                <InputNumber<number | string>
                  style={{ width: '100%' }}
                  placeholder="Ej: 500.000"
                  prefix="$"
                  min={0}
                  max={1000000000}
                  controls={false}
                  formatter={thousandsFormatter}
                  parser={thousandsParser}
                  onKeyDown={blockNegativeKeys}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={16}>
              <Form.Item
                name="damagedParts"
                label="Indicar partes o piezas dañadas del vehículo *"
                normalize={stripSpecialChars}
                rules={[{ required: true, message: 'Debe indicar las partes o piezas dañadas' }]}
              >
                <Input placeholder="Ej: Parachoques delantero, foco derecho" maxLength={500} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="hasRepairBudget" label="¿Tiene presupuesto de reparación?">
                <YesNoRadio />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {/* ── Rama: Otro bien y/o persona ────────────────────────── */}
      {propertyType === 'OTRO' && (
        <>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="damagedGoods"
                label="Tipo de bien y/o lesión física *"
                normalize={stripSpecialChars}
                rules={[{ required: true, message: 'Debe indicar el tipo de bien y/o lesión física' }]}
              >
                <Input placeholder="Ej: Reja, muro perimetral, lesión en brazo" maxLength={500} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="claimedAmount"
                label="Valor a Reclamar *"
                rules={[
                  { required: true, message: 'Debe ingresar el valor a reclamar' },
                  {
                    type: 'number',
                    min: 0,
                    transform: (value) => (value === '' || value === undefined ? undefined : Number(value)),
                    message: 'El valor no puede ser negativo',
                  },
                  {
                    type: 'number',
                    max: 1000000000,
                    transform: (value) => (value === '' || value === undefined ? undefined : Number(value)),
                    message: 'El valor no puede ser mayor a 1.000.000.000',
                  },
                  {
                    // Advertencia (no bloqueante): avisa si el valor a reclamar es 0.
                    warningOnly: true,
                    validator: (_, value) =>
                      value !== '' && value !== undefined && value !== null && Number(value) === 0
                        ? Promise.reject(new Error('El valor ingresado es 0, verifique que sea correcto'))
                        : Promise.resolve(),
                  },
                ]}
              >
                <InputNumber<number | string>
                  style={{ width: '100%' }}
                  placeholder="Ej: 500.000"
                  prefix="$"
                  min={0}
                  max={1000000000}
                  controls={false}
                  formatter={thousandsFormatter}
                  parser={thousandsParser}
                  onKeyDown={blockNegativeKeys}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="damageInfo"
                label="Detalle del daño *"
                normalize={stripSpecialChars}
                rules={[{ required: true, message: 'Debe describir el detalle del daño' }]}
              >
                <TextArea rows={3} placeholder="Describa el detalle del daño..." maxLength={500} showCount />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}

// ─── Fila de documento (tabla Adjuntar Información) ──────────────────────────

interface DocRowProps {
  nameNode: React.ReactNode;
  uploaded: boolean;
  filename?: string | null;
  uploading?: boolean;
  canAttach?: boolean;
  attachTooltip?: string;
  onAttach: (file: File) => void;
  onView: () => void;
  onDelete: () => void;
  showDelete: boolean;
}

function DocRow({
  nameNode,
  uploaded,
  filename,
  uploading,
  canAttach = true,
  attachTooltip,
  onAttach,
  onView,
  onDelete,
  showDelete,
}: DocRowProps) {
  return (
    <Row gutter={[16, 16]} justify="center" className="classRowAdjunto">
      <Col span={8}>
        <span className="text_obligatorio">Opcional</span>
      </Col>
      <Col span={8}>{nameNode}</Col>
      <Col span={3}>
        <Upload
          showUploadList={false}
          disabled={!canAttach || uploading}
          beforeUpload={(file) => {
            if (!isValidDocFile(file as unknown as File)) return false;
            onAttach(file as unknown as File);
            return false;
          }}
          accept={DOC_ACCEPT_ATTR}
        >
          <Tooltip title={attachTooltip || (uploaded ? 'Reemplazar archivo' : 'Adjuntar archivo')}>
            <div className="upload_file_row">
              {uploading ? (
                <LoadingOutlined />
              ) : (
                <UploadOutlined className={!canAttach ? 'upload_disabled' : ''} />
              )}
            </div>
          </Tooltip>
        </Upload>
      </Col>
      <Col span={3}>
        {uploaded ? (
          <Button type="text" className="btn_add" onClick={onView}>
            <Tooltip title={filename || 'Visualizar'}>
              <EyeOutlined />
            </Tooltip>
          </Button>
        ) : (
          <a className="btn_add_disbled">
            <EyeInvisibleOutlined />
          </a>
        )}
      </Col>
      <Col span={2}>
        {showDelete && (
          <Button type="text" className="btn_add" onClick={onDelete}>
            <Tooltip title="Eliminar">
              <DeleteOutlined />
            </Tooltip>
          </Button>
        )}
      </Col>
    </Row>
  );
}

// ─── Step 4: Adjuntar Información ─────────────────────────────────────────────

function Step4Documentos({
  namedDocs,
  setNamedDocs,
  additionalSlots,
  setAdditionalSlots,
}: {
  namedDocs: UploadedDoc[];
  setNamedDocs: React.Dispatch<React.SetStateAction<UploadedDoc[]>>;
  additionalSlots: AdditionalSlot[];
  setAdditionalSlots: React.Dispatch<React.SetStateAction<AdditionalSlot[]>>;
}) {
  const handleNamedUpload = async (index: number, file: File) => {
    const updated = [...namedDocs];
    updated[index] = { ...updated[index], uploading: true };
    setNamedDocs(updated);
    try {
      // Tipo único (TPC_DOCUMENT_TYPE); el nombre de archivo lleva el `key` de la fila
      // para que no colisionen en S3 (antes los distinguía el documentType).
      const response = await uploadFiles(file, TPC_DOCUMENT_TYPE, `${namedDocs[index].key || index}_${file.name}`);
      const uploaded = [...namedDocs];
      uploaded[index] = {
        ...uploaded[index],
        filename: response.data[0]?.filename || file.name,
        temporaryKey: response.data[0]?.filename || file.name,
        uploading: false,
      };
      setNamedDocs(uploaded);
      message.success(`"${namedDocs[index].documentName}" subido correctamente`);
    } catch (err) {
      const updated2 = [...namedDocs];
      updated2[index] = { ...updated2[index], uploading: false };
      setNamedDocs(updated2);
      const e = err as any;
      if (e?.status === 413) {
        message.error('El archivo supera el tamaño máximo permitido');
      } else {
        message.error('Error al subir el archivo');
      }
    }
  };

  const handleRemoveNamed = (index: number) => {
    const updated = [...namedDocs];
    updated[index] = { ...updated[index], filename: null, temporaryKey: null };
    setNamedDocs(updated);
  };

  const handleAdditionalUpload = async (index: number, file: File, name: string) => {
    if (!name.trim()) {
      message.warning('Ingrese un nombre para el documento antes de subir');
      return;
    }
    const updated = [...additionalSlots];
    updated[index] = { ...updated[index], uploading: true };
    setAdditionalSlots(updated);
    try {
      // Tipo único (TPC_DOCUMENT_TYPE); el nombre de archivo lleva el índice del adicional
      // para que no colisionen en S3.
      const response = await uploadFiles(file, TPC_DOCUMENT_TYPE, `adicional_${index}_${file.name}`);
      const updated2 = [...additionalSlots];
      updated2[index] = {
        ...updated2[index],
        filename: response.data[0]?.filename || file.name,
        temporaryKey: response.data[0]?.filename || file.name,
        uploading: false,
      };
      setAdditionalSlots(updated2);
      message.success('Documento adicional subido correctamente');
    } catch (err) {
      const updated2 = [...additionalSlots];
      updated2[index] = { ...updated2[index], uploading: false };
      setAdditionalSlots(updated2);
      const e = err as any;
      if (e?.status === 413) {
        message.error('El archivo supera el tamaño máximo permitido');
      } else {
        message.error('Error al subir el archivo');
      }
    }
  };

  const handleDeleteAdditionalSlot = (index: number) => {
    setAdditionalSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const addAdditionalSlot = () => {
    if (additionalSlots.length >= MAX_ADDITIONAL_DOCS) {
      message.warning(`Máximo ${MAX_ADDITIONAL_DOCS} documentos adicionales`);
      return;
    }
    setAdditionalSlots((prev) => [...prev, { name: '', filename: null, temporaryKey: null }]);
  };

  const handleView = async (temporaryKey: string | null, filename: string | null) => {
    if (!temporaryKey) return;
    try {
      const { data } = await getUrlFile(temporaryKey);
      const url = typeof data === 'string' ? data : data?.url || data?.data || '';
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        message.error('No se pudo obtener el archivo para visualizar');
      }
    } catch {
      message.error(`No se pudo visualizar "${filename || 'el archivo'}"`);
    }
  };

  return (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: '16px', fontSize: '13px' }}>
        En esta sección puede adjuntar los documentos relacionados con su reclamo. Todos los documentos son
        opcionales, con un peso máximo de 10 MB por archivo (documentos, imágenes o videos).
      </Text>

      <div className="attach_document">
        {/* Encabezado — mismo estilo visual que Declaración de Siniestro */}
        <Row gutter={[16, 16]} justify="center" style={{ padding: '0 13px', maxWidth: '98%' }}>
          <Col span={8}>
            <label className="classLabel">Requerimiento</label>
          </Col>
          <Col span={8}>
            <label className="classLabel">Nombre</label>
          </Col>
          <Col span={3}>
            <label className="classLabel">Adjuntar</label>
          </Col>
          <Col span={3}>
            <label className="classLabel">Visualizar</label>
          </Col>
          <Col span={2}>
            <label className="classLabel" />
          </Col>
        </Row>

        {/* Documentos predefinidos */}
        {namedDocs.map((doc, index) => (
          <DocRow
            key={doc.documentName}
            nameNode={doc.documentName}
            uploaded={!!doc.temporaryKey}
            filename={doc.filename}
            uploading={doc.uploading}
            onAttach={(file) => handleNamedUpload(index, file)}
            onView={() => handleView(doc.temporaryKey, doc.filename)}
            onDelete={() => handleRemoveNamed(index)}
            showDelete={!!doc.temporaryKey}
          />
        ))}

        {/* Documentos adicionales, con el mismo estilo de fila */}
        {additionalSlots.map((slot, index) => {
          const hasName = !!slot.name.trim();
          return (
            <DocRow
              key={`additional-${index}`}
              nameNode={
                <Input
                  bordered={false}
                  className="tpc-doc-name-input"
                  placeholder="Nombre del documento adicional"
                  value={slot.name}
                  maxLength={60}
                  onChange={(e) => {
                    const updated = [...additionalSlots];
                    updated[index] = { ...updated[index], name: e.target.value };
                    setAdditionalSlots(updated);
                  }}
                />
              }
              uploaded={!!slot.temporaryKey}
              filename={slot.filename}
              uploading={slot.uploading}
              canAttach={hasName}
              attachTooltip={hasName ? undefined : 'Ingrese un nombre antes de adjuntar'}
              onAttach={(file) => handleAdditionalUpload(index, file, slot.name)}
              onView={() => handleView(slot.temporaryKey, slot.filename)}
              onDelete={() => handleDeleteAdditionalSlot(index)}
              showDelete
            />
          );
        })}

        {additionalSlots.length < MAX_ADDITIONAL_DOCS && (
          <Button
            className="btn_add"
            icon={<PlusOutlined />}
            onClick={addAdditionalSlot}
            style={{ marginTop: 16 }}
          >
            Agregar otro documento
          </Button>
        )}
      </div>
    </>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

function ThirdPartyClaimPage() {
  const [step, setStep] = useState(0);
  // Etapa máxima alcanzada: define qué etapas están desbloqueadas (verdes/clickeables).
  const [maxStepReached, setMaxStepReached] = useState(0);
  // Al cambiar de etapa, subir la vista a la cabecera de la página (sobre el banner).
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const [claimantRole, setClaimantRole] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successShortId, setSuccessShortId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [form] = Form.useForm();

  const [namedDocs, setNamedDocs] = useState<UploadedDoc[]>(
    NAMED_DOCUMENTS.map((d) => ({
      key: d.key,
      documentName: d.label,
      filename: null,
      temporaryKey: null,
      isAdditional: false,
    }))
  );
  const [additionalSlots, setAdditionalSlots] = useState<AdditionalSlot[]>([]);

  const handleNext = async () => {
    if (!claimantRole) {
      message.error('Seleccione su rol en este proceso antes de continuar');
      window.scrollTo(0, 0);
      return;
    }
    try {
      await form.validateFields();
      setStep((prev) => prev + 1);
      setMaxStepReached((m) => Math.max(m, step + 1));
      scrollToTop();
    } catch {
      message.error('Complete los campos requeridos antes de continuar');
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
    // Al cambiar de etapa, subir a la cabecera de la página (sobre el banner).
    scrollToTop();
  };

  // Navegación por el índice de etapas: solo a etapas ya alcanzadas (desbloqueadas).
  // Hacia atrás es libre; hacia adelante valida la etapa actual antes de saltar.
  const goToStep = async (target: number) => {
    if (target === step || target > maxStepReached) return;
    if (target < step) {
      setStep(target);
      scrollToTop();
      return;
    }
    try {
      await form.validateFields();
      setStep(target);
      scrollToTop();
    } catch {
      message.error('Complete los campos requeridos antes de continuar');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const values = form.getFieldsValue(true);

      // El paso 3 es EXCLUYENTE: solo se declara una rama (Vehículo u Otro bien).
      // Se envían únicamente los campos de la rama seleccionada; los de la otra
      // rama van null aunque AntD conserve sus valores ocultos, para no guardar
      // ambos formularios a la vez.
      const isVehicle = values.affectedPropertyType === 'VEHICULO';
      const isOtherProperty = values.affectedPropertyType === 'OTRO';

      const documents = [
        ...namedDocs
          .filter((d) => d.temporaryKey)
          .map((d) => ({
            documentName: d.documentName,
            filename: d.filename,
            temporaryKey: d.temporaryKey,
            isAdditional: false,
          })),
        ...additionalSlots
          .filter((s) => s.temporaryKey && s.name.trim())
          .map((s) => ({
            documentName: s.name,
            filename: s.filename,
            temporaryKey: s.temporaryKey,
            isAdditional: true,
          })),
      ];

      const payload = {
        claimantRole,
        gamaVehiclePlate: values.gamaVehiclePlate,
        gamaDriverName: values.gamaDriverName,
        driverIdType: values.driverIdType,
        gamaDriverId: values.driverIdType === 'PASAPORTE' ? values.gamaDriverPassport : values.gamaDriverRut,
        // Fecha-calendario (columna `date`): se envía como YYYY-MM-DD sin zona,
        // igual que el estándar de Siniestro (claimDate/lossDeclaredDate). Antes
        // usaba toISOString() y el instante UTC provocaba corrimientos de día.
        accidentDate: values.accidentDate?.format('YYYY-MM-DD'),
        accidentTime: values.accidentTime?.format('HH:mm') || null,
        accidentLocation: values.accidentLocation,
        accidentRegion: values.accidentRegion || null,
        accidentCommune: values.accidentCommune || null,
        accidentDescription: values.accidentDescription,
        claimantFullName: values.claimantFullName,
        claimantIdType: values.claimantIdType,
        claimantId:
          values.claimantIdType === 'PASAPORTE' ? values.claimantPassport : values.claimantRut,
        claimantEmails: [values.claimantEmail, values.claimantEmailAlt]
          .filter(Boolean)
          .filter((email, index, arr) => arr.indexOf(email) === index),
        claimantPhone: values.claimantPhone,
        thirdPartyRegion: values.thirdPartyRegion || null,
        thirdPartyCommune: values.thirdPartyCommune || null,
        institutionName: values.institutionName || null,
        institutionRut: values.institutionRut || null,
        affectedPropertyType: values.affectedPropertyType,
        // Datos del dueño del vehículo y presupuesto (solo rama vehículo)
        thirdPartyOwnerName: isVehicle ? values.thirdPartyOwnerName || null : null,
        thirdPartyOwnerId: isVehicle
          ? (values.thirdPartyOwnerIdType === 'PASAPORTE' ? values.thirdPartyOwnerPassport : values.thirdPartyOwnerRut) ||
            null
          : null,
        claimedAmount: values.claimedAmount ? Number(values.claimedAmount) : null,
        hasRepairBudget: isVehicle && typeof values.hasRepairBudget === 'boolean' ? values.hasRepairBudget : null,
        // Rama vehículo (solo si la propiedad afectada es un vehículo)
        thirdPartyVehicleType: isVehicle ? values.thirdPartyVehicleType || null : null,
        thirdPartyVehiclePlate: isVehicle ? values.thirdPartyVehiclePlate || null : null,
        thirdPartyVehicleBrand: isVehicle ? values.thirdPartyVehicleBrand || null : null,
        thirdPartyVehicleModel: isVehicle ? values.thirdPartyVehicleModel || null : null,
        thirdPartyVehicleYear: isVehicle && values.thirdPartyVehicleYear ? Number(values.thirdPartyVehicleYear) : null,
        gamaMediaType: isVehicle ? values.gamaMediaType || null : null,
        thirdPartyHasInsurance: isVehicle && typeof values.thirdPartyHasInsurance === 'boolean' ? values.thirdPartyHasInsurance : null,
        insuranceClaimNumber: isVehicle ? values.insuranceClaimNumber || null : null,
        insuranceCompanyName: isVehicle ? values.insuranceCompanyName || null : null,
        hasBodilyInjuries: isVehicle && typeof values.hasBodilyInjuries === 'boolean' ? values.hasBodilyInjuries : null,
        hasAlcoholTest: isVehicle && typeof values.hasAlcoholTest === 'boolean' ? values.hasAlcoholTest : null,
        damagedParts: isVehicle ? values.damagedParts || null : null,
        // Rama otro bien / persona (solo si la propiedad afectada es "Otro bien")
        damagedGoods: isOtherProperty ? values.damagedGoods || null : null,
        damageInfo: isOtherProperty ? values.damageInfo || null : null,
        documents,
      };

      const { data } = await createThirdPartyClaim(payload);
      setSuccessShortId(data?.shortId || '');
      setSubmitted(true);
    } catch (error) {
      message.error('Ocurrió un error al enviar el reclamo. Inténtelo nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetWizard = () => {
    setStep(0);
    setClaimantRole('');
    setSuccessShortId('');
    setSubmitted(false);
    form.resetFields();
    setNamedDocs(
      NAMED_DOCUMENTS.map((d) => ({
        key: d.key,
        documentName: d.label,
        filename: null,
        temporaryKey: null,
        isAdditional: false,
      }))
    );
    setAdditionalSlots([]);
  };

  const isLastStep = step === 3;

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="tpc-layout">
        <div className="tpc-aside">
          <SidebarSteps current={3} maxReached={STEP_NAMES.length - 1} onStepClick={() => {}} />
        </div>
        <div className="tpc-form">
          <div className="tpc-success">
            <CheckCircleOutlined className="tpc-success-icon" />
            <Title level={2} style={{ marginTop: '16px' }}>
              ¡Reclamo registrado exitosamente!
            </Title>
            <Paragraph style={{ color: '#595959' }}>
              Su reclamo ha sido recibido y será procesado a la brevedad.
            </Paragraph>
            {successShortId && (
              <div className="tpc-success-id">
                <Text type="secondary">Número de seguimiento</Text>
                <br />
                <span>{successShortId}</span>
              </div>
            )}
            <Paragraph type="secondary">
              Guarde este número para hacer seguimiento de su reclamo.
            </Paragraph>
            <Button style={{ ...primaryBtn, marginTop: '24px' }} onClick={resetWizard}>
              Realizar otro reclamo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main two-column layout ──────────────────────────────────────────────────
  return (
    <div className="tpc-layout">
      {/* Sidebar */}
      <div className="tpc-aside">
        <SidebarSteps current={step} maxReached={maxStepReached} onStepClick={goToStep} />
      </div>

      {/* Right panel */}
      <div className="tpc-form">
        {/* Role selector */}
        <div className="tpc-role-selector">
          <div className="tpc-role-title">Seleccione su rol en este proceso: *</div>
          <Radio.Group
            value={claimantRole}
            onChange={(e) => setClaimantRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((opt) => (
              <Radio
                key={opt.value}
                value={opt.value}
                // Una vez elegido el rol, las otras opciones quedan bloqueadas (no se puede cambiar).
                disabled={!!claimantRole && claimantRole !== opt.value}
              >
                {opt.label}
              </Radio>
            ))}
          </Radio.Group>
        </div>

        <Divider style={{ marginTop: '8px', marginBottom: '20px' }} />

        {!claimantRole ? (
          /* Hasta que el Tercero no seleccione su rol, no se muestra ningún paso del
             formulario ni la navegación: solo la instrucción para elegirlo primero. */
          <div className="tpc-role-hint" style={{ color: '#8c8c8c', padding: '24px 0' }}>
            Seleccione su rol para comenzar.
          </div>
        ) : (
          <>
            {/* Step title */}
            <div className="tpc-step-title">
              {step + 1}. {STEP_NAMES[step]}
            </div>
            <div className="tpc-step-subtitle">
              Los campos marcados con el asterisco (*) son obligatorios.
            </div>

            {/* Form */}
            <Form form={form} layout="vertical" requiredMark={false} className="tpc-section">
              {step === 0 && <Step1Siniestro form={form} />}
              {step === 1 && <Step2Contacto form={form} claimantRole={claimantRole} />}
              {step === 2 && <Step3Bien form={form} />}
              {step === 3 && (
                <Step4Documentos
                  namedDocs={namedDocs}
                  setNamedDocs={setNamedDocs}
                  additionalSlots={additionalSlots}
                  setAdditionalSlots={setAdditionalSlots}
                />
              )}
            </Form>

            {/* Navigation */}
            <div className="tpc-nav">
              {step === 0 ? (
                <Link to={HOME_ROUTE} onClick={() => window.scrollTo(0, 0)}>
                  <Button style={outlineBtn}>Cancelar</Button>
                </Link>
              ) : (
                <Button style={outlineBtn} onClick={handleBack}>
                  Atrás
                </Button>
              )}

              {!isLastStep ? (
                <Button style={primaryBtn} onClick={handleNext}>
                  Siguiente
                </Button>
              ) : (
                <Button style={primaryBtn} loading={submitting} onClick={handleSubmit}>
                  Enviar
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ThirdPartyClaimPage;
